import { compareByCodePoint, type TicketType } from '@vd/types';

/**
 * A draft edited on the review page while its moment was filed as a ticket
 * during the recording (TOADO-900, review rounds 1 to 3). The card turns into
 * the ticket's editor and the commit no longer takes a draft for it, so what
 * the person changed is handed to that editor as unsaved edits, and they save
 * it there. Never sent in the background: a request the person cannot see can
 * arrive after a save they made in the editor, and put back older words.
 */
export type DraftSegmentType = 'bug' | 'feature_request' | null;

export interface CarriedEdits {
  title?: string;
  description?: string;
  /**
   * A bug or feature choice, resolved to the workspace's type by the editor;
   * null when the person cleared the choice (Codex review round 9), carried
   * as "No type" rather than lost to the type the moment was filed with.
   */
  segmentType?: 'bug' | 'feature_request' | null;
}

/**
 * The workspace's type for a recorder bug or feature choice: its first type
 * with that glyph, by order then id. The rule `ticketTypeIdsForSegmentTypes`
 * applies on the server when a moment is filed, so a type carried here is the
 * one the ticket would have been filed with.
 */
export function ticketTypeForSegmentType(types: readonly Pick<TicketType, 'id' | 'glyph' | 'order'>[], segmentType: 'bug' | 'feature_request'): string | null {
  const glyph = segmentType === 'bug' ? 'bug' : 'feature';
  const match = types
    .filter((type) => type.glyph === glyph)
    .sort((a, b) => a.order - b.order || compareByCodePoint(a.id, b.id))[0];
  return match?.id ?? null;
}

/**
 * What the person changed, or null when nothing: only those fields, so an
 * untouched description does not stand in for the ticket's write-up.
 */
export function draftEditsForTicket(
  draft: { title: string; description: string; segmentType: DraftSegmentType },
  saved: { title: string; description: string; segmentType: DraftSegmentType },
): CarriedEdits | null {
  const edits: CarriedEdits = {};
  // A cleared title too (z.ai review round 8 of the page): the editor shows it
  // empty and will not save it so, which says more than the old one coming back.
  if (draft.title !== saved.title) edits.title = draft.title;
  if (draft.description !== saved.description) edits.description = draft.description;
  if (draft.segmentType !== saved.segmentType) edits.segmentType = draft.segmentType;
  return Object.keys(edits).length > 0 ? edits : null;
}
